import { AsyncPipe, NgClass } from '@angular/common';
import {
    ChangeDetectionStrategy,
    Component,
    DestroyRef,
    ElementRef,
    OnInit,
    ViewChild,
    computed,
    inject,
} from '@angular/core';
import { takeUntilDestroyed, toObservable } from '@angular/core/rxjs-interop';
import { selectCurrentTheme } from '@interacta-shared/data-access-theme';
import {
    BypassSecurityTrustPipe,
    IconButtonComponent,
    ImageComponent,
    LoaderComponent,
} from '@interacta-shared/ui';
import { getLogoFromTheme } from '@modules/chat/helpers/chat.utils';
import { AppState } from '@modules/core/store';
import { ChatStateService } from '@modules/state/services/chat-state.service';
import { StateService } from '@modules/state/services/state.service';
import { Store } from '@ngrx/store';
import { TranslatePipe } from '@ngx-translate/core';
import { BehaviorSubject, combineLatest, distinctUntilChanged } from 'rxjs';
import { ChatPanelHeaderComponent } from '../chat-panel-header/chat-panel-header.component';

@Component({
    selector: 'interacta-chat-panel',
    templateUrl: './chat-panel.component.html',
    changeDetection: ChangeDetectionStrategy.OnPush,
    imports: [
        ChatPanelHeaderComponent,
        IconButtonComponent,
        NgClass,
        ImageComponent,
        LoaderComponent,
        AsyncPipe,
        TranslatePipe,
        BypassSecurityTrustPipe,
    ],
})
export class ChatPanelComponent implements OnInit {
    private readonly chatStateService = inject(ChatStateService);
    private readonly destroyRef = inject(DestroyRef);
    private readonly store = inject(Store<AppState>);
    readonly appState = inject(StateService);

    private chat?: ElementRef<HTMLIFrameElement>;

    @ViewChild('chat') set chatFrame(value: ElementRef<HTMLIFrameElement>) {
        requestAnimationFrame(() => {
            this.chat = value;
            this.renderedIframe.next(!!value?.nativeElement);
        });
    }

    readonly ANIMATION_DURATION = 150;

    readonly theme = this.store.selectSignal(selectCurrentTheme);

    renderedIframe = new BehaviorSubject(false);
    registered = false;
    chatLogo = computed(() => getLogoFromTheme(this.theme().mode));

    constructor() {
        toObservable(this.theme)
            .pipe(takeUntilDestroyed())
            .subscribe((theme) => {
                if (this.chatStateService.state.chatUrl$.getValue() === '') {
                    this.chatStateService.setChatUrl(theme);
                } else {
                    this.chatStateService.changeTheme(theme);
                }
            });
    }

    ngOnInit(): void {
        this.appState.chatState.isCollapsed$
            .pipe(takeUntilDestroyed(this.destroyRef))
            .subscribe((value) => {
                if (!this.chat) return;

                if (value) {
                    setTimeout(() => {
                        this.chatStateService.updateChatVisibility(
                            'background',
                        );
                    }, this.ANIMATION_DURATION);
                } else {
                    this.chatStateService.updateChatVisibility('foreground');
                    if (this.chatStateService.isOpen())
                        this.chat?.nativeElement.focus();
                }
            });

        combineLatest([this.appState.chatState.canShow$, this.renderedIframe])
            .pipe(
                distinctUntilChanged(
                    ([prevEnabled, prevRender], [currEnabled, currRender]) =>
                        (prevEnabled && prevRender) ===
                        (currEnabled && currRender),
                ),
                takeUntilDestroyed(this.destroyRef),
            )
            .subscribe(([isEnabled, renderIframe]) => {
                if (!this.registered && isEnabled && renderIframe) {
                    this.register();
                } else {
                    this.unregister();
                }
            });
    }

    toggleCollapsed(): void {
        this.chatStateService.toggleCollapse();
    }

    openChatInNewTab(): void {
        this.chatStateService.openInNewTab();
    }

    close(): void {
        this.chatStateService.close();
    }

    private register(): void {
        if (!this.chat?.nativeElement?.contentWindow) return;

        this.chatStateService.registerChat(
            this.chat.nativeElement.contentWindow,
        );

        this.registered = true;
    }

    private unregister(): void {
        //do nothing
    }
}
